import { RED_FLAG_CATEGORY_IDS, type RedFlagCategoryId } from './constants';
import type { BrandDealRewriteItem } from './rewriteTypes';
import type { BrandDealRewriteMode } from './rewriteTypes';

function isCategoryId(s: string): s is RedFlagCategoryId {
  return (RED_FLAG_CATEGORY_IDS as readonly string[]).includes(s);
}

function asNonEmptyString(v: unknown, maxLen: number): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (!t) return null;
  return t.length > maxLen ? t.slice(0, maxLen) + '...' : t;
}

const MAX_FULL_CONTRACT = 48000;
const MIN_FULL_CONTRACT_CHARS = 40;
const MAX_REWRITES_PUBLIC = 12;
const MAX_REWRITES_PERSONALIZED = 14;

/**
 * Models often omit or rename the full-draft field; accept common aliases.
 */
function extractFullRewrittenContract(raw: Record<string, unknown>): string | null {
  const keys = [
    'full_rewritten_contract',
    'fullRewrittenContract',
    'full_rewritten_agreement',
    'rewritten_contract',
    'rewritten_contract_full',
    'complete_rewritten_contract',
    'safer_draft',
    'safer_draft_full',
    'draft_contract',
    'revised_contract',
  ];
  for (const k of keys) {
    const s = asNonEmptyString(raw[k], MAX_FULL_CONTRACT);
    if (s && s.length >= MIN_FULL_CONTRACT_CHARS) return s;
  }
  return null;
}

/** If the model skipped a unified draft, assemble a readable full section from clause rewrites. */
export function stitchedFullDraftFromRewrites(rewrites: BrandDealRewriteItem[]): string {
  const header =
    'Creator-friendlier draft assembled from the clause-level suggestions. Review with the clause-by-clause notes below and with the brand. This is not legal advice.';
  const parts = rewrites.map((r) => `## ${r.title}\n\n${r.suggested_replacement}`);
  return `${header}\n\n${parts.join('\n\n---\n\n')}`;
}

export type NormalizedRewriteResult =
  | { ok: true; rewrites: BrandDealRewriteItem[]; full_rewritten_contract?: string }
  | { ok: false; message: string };

function parseRewritesArray(arr: unknown, maxItems: number): BrandDealRewriteItem[] | null {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const out: BrandDealRewriteItem[] = [];
  for (const item of arr) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const cat = typeof o.category_id === 'string' ? o.category_id.trim() : '';
    if (!isCategoryId(cat)) continue;
    const title = asNonEmptyString(o.title, 200);
    const excerptRaw = asNonEmptyString(o.original_excerpt, 500);
    const suggested = asNonEmptyString(o.suggested_replacement, 4000);
    const why = asNonEmptyString(o.why_this_is_better, 800);
    const highlightRaw = asNonEmptyString(o.highlight_phrase, 120);
    if (!title || !suggested || !why) continue;
    const excerpt = excerptRaw ?? '(see contract)';
    let highlight_phrase: string | undefined;
    if (highlightRaw && excerpt.toLowerCase().includes(highlightRaw.toLowerCase())) {
      highlight_phrase = highlightRaw;
    }
    out.push({
      category_id: cat,
      title,
      original_excerpt: excerpt,
      ...(highlight_phrase ? { highlight_phrase } : {}),
      suggested_replacement: suggested,
      why_this_is_better: why,
    });
  }
  if (out.length === 0) return null;
  return out.slice(0, maxItems);
}

export function normalizeRewriteModelOutput(
  raw: Record<string, unknown>,
  options: { mode: BrandDealRewriteMode },
): NormalizedRewriteResult {
  if (raw.status !== 'success') {
    return { ok: false, message: 'Unexpected model status for rewrite.' };
  }

  const maxRewrites = options.mode === 'personalized' ? MAX_REWRITES_PERSONALIZED : MAX_REWRITES_PUBLIC;
  const rewrites = parseRewritesArray(raw.rewrites, maxRewrites);
  if (!rewrites) {
    return { ok: false, message: 'No rewrites returned.' };
  }

  if (options.mode === 'personalized') {
    let fullRaw = extractFullRewrittenContract(raw);
    if (!fullRaw) {
      fullRaw = stitchedFullDraftFromRewrites(rewrites);
    }
    if (!fullRaw || fullRaw.length < MIN_FULL_CONTRACT_CHARS) {
      return { ok: false, message: 'Personalized rewrite did not return a full draft.' };
    }
    return { ok: true, rewrites, full_rewritten_contract: fullRaw };
  }

  return { ok: true, rewrites };
}
